/* eslint-disable react/display-name */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import HyroFeedbackMessage from "@hyro-components/HyroFeedbackMessage";
import {
  isDialogLoadingSelector,
  isPageLoadingSelector,
  isSavingChangesSelector
} from "src/stores/selectors/collectionsSelectors";
import { deleteWidgetCookie } from "@utils";
import { WIDGET_TOKEN } from "@consts";

import EditModeHeader from "./header-types/edit-mode-header/EditModeHeader";
import PreviewModeHeader from "./header-types/PreviewModeHeader";
import ReadOnlyModeHeader from "./header-types/ReadOnlyModeHeader";
import { Header } from "./HeaderStyles";
import Snippet from "../../components/widgets/overview/Snippet";
import { useIsAdminPanel } from "@hooks";
import useAppStore from "../../stores/appStore";
import {
  isWebSnippetOpenSelector,
  previousSystemModeSelector,
  selectedAccountIdSelector,
  selectedAssistantSelector,
  setIsAssistantPreviewableSelector,
  showHeaderSkeletonSelector,
  systemModeSelector
} from "../../stores/selectors/appSelectors";
import { SYSTEM_MODES } from "../../stores/slices/createAppSlice";
import { COLORS } from "../consts";
import { useDraft } from "src/queries/hooks/assistants";
import { WIDGET_SERVER_ENVIRONMENTS } from "src/utils/helpers/widgetServerUrl";

const HeaderComponent = React.memo(() => {
  const showHeaderSkeleton = useAppStore(showHeaderSkeletonSelector);
  const isWebSnippetOpen = useAppStore(isWebSnippetOpenSelector);
  const systemMode = useAppStore(systemModeSelector);
  const previousSystemMode = useAppStore(previousSystemModeSelector);
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const setIsAssistantPreviewable = useAppStore(setIsAssistantPreviewableSelector);
  const accountId = useAppStore(selectedAccountIdSelector);
  const isSavingChanges = useAppStore(isSavingChangesSelector);
  const isDialogLoading = useAppStore(isDialogLoadingSelector);
  const isPageLoading = useAppStore(isPageLoadingSelector);
  const isCollectionUpdating = isSavingChanges || isDialogLoading;
  const isAdminPanel = useIsAdminPanel();
  const { data: draft } = useDraft();
  const [feedbackConfig, setFeedbackConfig] = useState({
    open: false,
    message: "",
    severity: ""
  });
  const isDraftSnippet = previousSystemMode === SYSTEM_MODES.EDIT && !!draft;
  const widgetServerEnvironment = isDraftSnippet ? WIDGET_SERVER_ENVIRONMENTS.DRAFT : WIDGET_SERVER_ENVIRONMENTS.PREVIEW;

  useEffect(() => {
    if ((systemMode === SYSTEM_MODES.EDIT && previousSystemMode === SYSTEM_MODES.READ_ONLY)
      || (systemMode === SYSTEM_MODES.READ_ONLY && previousSystemMode === SYSTEM_MODES.EDIT)) {
      deleteWidgetCookie(WIDGET_TOKEN);
    }
  }, [systemMode, previousSystemMode]);

  useEffect(() => {
    setIsAssistantPreviewable();
  }, [
    accountId,
    isPageLoading,
    isCollectionUpdating,
    setIsAssistantPreviewable,
    systemMode
  ]);

  const getHeaderType = useCallback(() => {
    if (systemMode === SYSTEM_MODES.READ_ONLY || isAdminPanel) {
      return <ReadOnlyModeHeader selectedAssistant={selectedAssistant} />;
    }

    if (systemMode === SYSTEM_MODES.EDIT) {
      return <EditModeHeader selectedAssistant={selectedAssistant} />;
    }

    if (systemMode === SYSTEM_MODES.PREVIEW && selectedAssistant) {
      return <PreviewModeHeader />;
    }
    return <></>;
  }, [systemMode, isAdminPanel, selectedAssistant]);

  const headerColor = useMemo(() => {
    if (systemMode === SYSTEM_MODES.READ_ONLY || isAdminPanel) {
      return COLORS.HEADER_READ_ONLY_BG;
    }

    if (systemMode === SYSTEM_MODES.EDIT) {
      return COLORS.HEADER_EDIT_BG;
    }

    if (systemMode === SYSTEM_MODES.PREVIEW) {
      return previousSystemMode === SYSTEM_MODES.EDIT ? COLORS.HEADER_EDIT_BG : COLORS.HEADER_READ_ONLY_BG;
    }
    return COLORS.HEADER_READ_ONLY_BG;
  }, [systemMode, isAdminPanel, previousSystemMode]);

  return (
    <Header
      showHeaderSkeleton={showHeaderSkeleton}
      editBackground={headerColor}
      isPreviewMode={systemMode === SYSTEM_MODES.PREVIEW}>
      {getHeaderType()}
      <Snippet
        open={isWebSnippetOpen}
        widgetServerEnvironment={widgetServerEnvironment}
        assistantId={selectedAssistant?.assistantId} />
      <HyroFeedbackMessage
        showFeedback={feedbackConfig.open}
        closeFeedback={() => setFeedbackConfig({ ...feedbackConfig, open: false })}
        message={feedbackConfig.message}
        severity={feedbackConfig.severity}
      />
    </Header>
  );
});

export default HeaderComponent;
